/**
 * Resolve remote passage markdown (import V3 GenResultNode media_url) into page content.
 */

import type { PageElementData } from '@ai-content-hub/content-elements';
import { defaultPageData } from '@ai-content-hub/content-elements';
import { looksLikeMarkdown, markdownToTiptap } from '@ai-content-hub/content-kit';

const SECTION_LAYOUT = {
    type: 'vertical' as const,
    gap: '1rem',
    alignment: 'stretch' as const,
};

/** Minimal plain-text → Tiptap doc (player-only fallback when BE did not inline passage). */
function plainTextToTiptapDoc(text: string): Record<string, unknown> {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) {
        return { type: 'doc', content: [{ type: 'paragraph' }] };
    }
    return {
        type: 'doc',
        content: lines.map((line) => ({
            type: 'paragraph',
            attrs: { textAlign: null },
            content: [{ type: 'text', text: line }],
        })),
    };
}

const INLINE_IMAGE_MD = /^\s*!\[([^\]]*)\]\(([^)]+)\)\s*$/;

function passageMarkdownToPageSection(markdown: string): PageElementData['sections'] {
    const trimmed = markdown.trim();
    const lines = trimmed.split('\n').map((l) => l.trim()).filter(Boolean);

    if (lines.length > 0 && lines.every((line) => INLINE_IMAGE_MD.test(line))) {
        return [
            {
                id: 'section-passage-remote',
                layout: SECTION_LAYOUT,
                blocks: lines.map((line, idx) => {
                    const match = line.match(INLINE_IMAGE_MD);
                    const alt = match?.[1] ?? '';
                    const src = match?.[2] ?? '';
                    return {
                        id: `block-passage-remote-image-${idx + 1}`,
                        content: {
                            type: 'image' as const,
                            src,
                            alt,
                            caption: '',
                            size: 'large' as const,
                            alignment: 'center' as const,
                        },
                    };
                }),
            },
        ];
    }

    const doc = looksLikeMarkdown(trimmed)
        ? markdownToTiptap(trimmed)
        : plainTextToTiptapDoc(trimmed);

    return [
        {
            id: 'section-passage-remote',
            layout: SECTION_LAYOUT,
            blocks: [
                {
                    id: 'block-passage-remote',
                    content: {
                        type: 'rich-text',
                        content: doc,
                    },
                },
            ],
        },
    ];
}

export async function fetchPassageMarkdown(url: string): Promise<string> {
    const resp = await fetch(url);
    if (!resp.ok) {
        throw new Error(`Failed to fetch passage: ${resp.status}`);
    }
    return resp.text();
}

/**
 * Merge fetched passage markdown into pageContent when sections are empty
 * and ``passageUrl`` is set (import reuse path).
 */
export function mergePassageIntoPageContent(
    pageContent: PageElementData | undefined,
    passageMarkdown: string,
): PageElementData {
    const base = { ...defaultPageData, ...pageContent };
    const text = passageMarkdown.trim();
    if (!text) {
        return base;
    }
    const passageSections = passageMarkdownToPageSection(text);
    const existingSections = base.sections ?? [];
    if (existingSections.length === 0) {
        return {
            ...base,
            sections: passageSections,
        };
    }
    return {
        ...base,
        sections: [...existingSections, ...passageSections],
    };
}
